// ── BCH Live — Discovery, Scheduling & Overlays ───────────────────
//
// Built on top of `streamCredentials.ts`, which owns platform detection
// and kind 30311 parsing. This module adds the *product* layer:
//
//   • Categories        — a curated taxonomy carried in NIP-53 `t` tags
//   • Discovery ranking — multi-signal scoring (never viewers alone)
//   • Scheduling        — upcoming broadcasts via `status: planned` + `starts`
//   • Overlays          — per-creator on/off prefs for stream overlays
//
// Everything here stays inside NIP-53 Live Activities so other Nostr
// clients keep working with BCHNostr streams.

import type { StreamMeta } from './streamCredentials';

// ══════════════════════════════════════════════════════════════════
//  CATEGORIES
//
//  Stored as a NIP-53 `t` tag so relays index them and filters like
//  '#t': ['bch-cat-gaming'] work server-side. The `bch-cat-` prefix
//  keeps our taxonomy from colliding with free-form hashtags.
// ══════════════════════════════════════════════════════════════════

export const CATEGORY_TAG_PREFIX = 'bch-cat-';

export interface StreamCategory {
  /** Stable slug used in the `t` tag */
  id: string;
  label: string;
  /** Lucide icon name, resolved by the UI */
  icon: string;
  /** Tailwind gradient classes for the category tile */
  gradient: string;
}

export const STREAM_CATEGORIES: StreamCategory[] = [
  { id: 'bch-talk',    label: 'BCH Talk',      icon: 'Coins',      gradient: 'from-emerald-500 to-teal-600' },
  { id: 'trading',     label: 'Trading',       icon: 'TrendingUp', gradient: 'from-amber-500 to-orange-600' },
  { id: 'dev',         label: 'Development',   icon: 'Code2',      gradient: 'from-blue-500 to-indigo-600' },
  { id: 'gaming',      label: 'Gaming',        icon: 'Gamepad2',   gradient: 'from-violet-500 to-purple-600' },
  { id: 'music',       label: 'Music',         icon: 'Music',      gradient: 'from-pink-500 to-rose-600' },
  { id: 'irl',         label: 'IRL',           icon: 'Camera',     gradient: 'from-cyan-500 to-sky-600' },
  { id: 'education',   label: 'Education',     icon: 'GraduationCap', gradient: 'from-lime-500 to-green-600' },
  { id: 'news',        label: 'News',          icon: 'Newspaper',  gradient: 'from-slate-500 to-gray-600' },
  { id: 'merchants',   label: 'Merchants',     icon: 'Store',      gradient: 'from-fuchsia-500 to-pink-600' },
  { id: 'podcast',     label: 'Podcast',       icon: 'Mic2',       gradient: 'from-red-500 to-rose-600' },
  { id: 'art',         label: 'Art & Design',  icon: 'Palette',    gradient: 'from-orange-500 to-amber-600' },
  { id: 'other',       label: 'Other',         icon: 'Radio',      gradient: 'from-teal-500 to-emerald-600' },
];

const CATEGORY_BY_ID = new Map(STREAM_CATEGORIES.map(c => [c.id, c]));

export function getCategory(id: string | undefined | null): StreamCategory | null {
  if (!id) return null;
  return CATEGORY_BY_ID.get(id) || null;
}

/** Full relay-indexable tag value for a category id. */
export function categoryTagValue(id: string): string {
  return `${CATEGORY_TAG_PREFIX}${id}`;
}

/** Extract the first recognised category from an event's tags. */
export function extractCategory(tags: string[][]): StreamCategory | null {
  for (const t of tags) {
    if (t[0] !== 't' || !t[1]) continue;
    if (!t[1].startsWith(CATEGORY_TAG_PREFIX)) continue;
    const cat = CATEGORY_BY_ID.get(t[1].slice(CATEGORY_TAG_PREFIX.length));
    if (cat) return cat;
  }
  return null;
}

/**
 * Free-form hashtags, excluding our own bookkeeping tags.
 * These are the tags we surface on cards as "#gaming"-style chips.
 */
const RESERVED_TAGS = new Set(['bch', 'bitcoincash', 'bch-live', 'superchat', 'bch-tip']);

export function extractTopicTags(tags: string[][], limit = 4): string[] {
  const out: string[] = [];
  for (const t of tags) {
    if (t[0] !== 't' || !t[1]) continue;
    const v = t[1];
    if (RESERVED_TAGS.has(v)) continue;
    if (v.startsWith(CATEGORY_TAG_PREFIX)) continue;
    if (out.includes(v)) continue;
    out.push(v);
    if (out.length >= limit) break;
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════
//  EXTENDED STREAM MODEL
// ══════════════════════════════════════════════════════════════════

export interface LiveStream extends StreamMeta {
  /** Host pubkey from the `p`/Host tag, falling back to the event author */
  hostPubkey: string;
  category: StreamCategory | null;
  topics: string[];
  /** Parsed `starts` as a number (0 when absent) */
  startsAt: number;
  /** Parsed `ends` as a number (0 when absent) */
  endsAt: number;
  /** Numeric viewer count (0 when the tag is absent/unparseable) */
  viewerCount: number;
  /** True when the creator has a resolvable BCH address for tipping */
  tippingEnabled: boolean;
  /** Scheduled in the future and not yet live */
  isUpcoming: boolean;
  /** Was live, now ended */
  hasEnded: boolean;
}

function num(v: string | undefined | null): number {
  if (!v) return 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Enrich a parsed StreamMeta with discovery metadata.
 *
 * `tippingEnabled` defaults to true: BCH tipping works through the
 * creator's Nostr-derived address, so it is available unless we have
 * positive evidence otherwise. Callers can override once resolved.
 */
export function toLiveStream(meta: StreamMeta, tags: string[][]): LiveStream {
  const hostTag = tags.find(t => t[0] === 'p' && t[3] === 'Host');
  const hostPubkey = hostTag?.[1] || meta.pubkey;
  const startsAt = num(meta.startedAt);
  const endsAt = num(tags.find(t => t[0] === 'ends')?.[1]);
  const now = Math.floor(Date.now() / 1000);

  const isUpcoming = !meta.isLive
    && (meta.status === 'planned' || (startsAt > now && meta.status !== 'ended'));

  return {
    ...meta,
    hostPubkey,
    category: extractCategory(tags),
    topics: extractTopicTags(tags),
    startsAt,
    endsAt,
    viewerCount: num(meta.viewers),
    tippingEnabled: true,
    isUpcoming,
    hasEnded: !meta.isLive && !isUpcoming,
  };
}

// ══════════════════════════════════════════════════════════════════
//  DISCOVERY RANKING
//
//  Explicitly NOT "sort by viewers". Viewer counts on Nostr are
//  self-reported by the streaming client, trivially spoofed, and often
//  simply absent — ranking on them alone would both mislead and bury
//  small creators.
//
//  Instead we blend engagement signals, with BCH tipping weighted
//  highest because it is the one signal that costs real money and is
//  therefore the hardest to fake.
// ══════════════════════════════════════════════════════════════════

export interface StreamSignals {
  /** Distinct chat messages in the scoring window */
  chatCount: number;
  /** Number of BCH tips / Super Chats received */
  tipCount: number;
  /** Total USD tipped */
  tipUsd: number;
  /** Reactions (kind 7) on the stream */
  reactionCount: number;
  /** Reposts / shares of the stream */
  shareCount: number;
  /** Distinct chatters — a proxy for genuine audience breadth */
  uniqueChatters: number;
}

export const EMPTY_SIGNALS: StreamSignals = {
  chatCount: 0, tipCount: 0, tipUsd: 0,
  reactionCount: 0, shareCount: 0, uniqueChatters: 0,
};

/** Weights are tuned so money > people > noise. */
const W = {
  tipUsd: 9,        // strongest: real BCH, unfakeable
  tipCount: 5,      // many small tips beat one whale
  uniqueChatters: 4,
  chat: 1.4,
  reaction: 1.1,
  share: 2.5,
  viewer: 0.35,     // present but deliberately weak
  liveBonus: 40,    // live always outranks non-live
};

/**
 * Sub-linear damping. Keeps a single huge number (one $500 tip, a
 * viewer count of 50k) from dominating every other signal.
 */
function damp(v: number): number {
  return v <= 0 ? 0 : Math.log10(1 + v);
}

/**
 * Recency decay. A stream that started 20 minutes ago should beat one
 * that has been idling for 9 hours with the same engagement.
 * Half-life ≈ 3 hours.
 */
function recencyFactor(stream: LiveStream, now: number): number {
  const anchor = Math.max(stream.startsAt, stream.created_at, num(stream.heartbeat));
  const ageHours = Math.max(0, (now - anchor) / 3600);
  return Math.pow(0.5, ageHours / 3);
}

/**
 * Retention proxy: streams that keep chat moving relative to their age
 * are holding an audience. Bounded so a brand-new stream isn't punished.
 */
function retentionFactor(stream: LiveStream, s: StreamSignals, now: number): number {
  const anchor = Math.max(stream.startsAt, stream.created_at);
  const minutes = Math.max(5, (now - anchor) / 60);
  const perMinute = (s.chatCount + s.tipCount * 3) / minutes;
  return 1 + Math.min(0.8, perMinute * 0.5);
}

export function scoreStream(stream: LiveStream, signals: StreamSignals, now = Math.floor(Date.now() / 1000)): number {
  const s = signals;

  const engagement =
      damp(s.tipUsd) * W.tipUsd
    + damp(s.tipCount) * W.tipCount
    + damp(s.uniqueChatters) * W.uniqueChatters
    + damp(s.chatCount) * W.chat
    + damp(s.reactionCount) * W.reaction
    + damp(s.shareCount) * W.share
    + damp(stream.viewerCount) * W.viewer;

  const score = engagement
    * retentionFactor(stream, s, now)
    * (stream.isLive ? 1 : recencyFactor(stream, now));

  return score + (stream.isLive ? W.liveBonus : 0);
}

/** Human-readable reason a stream is trending, for UI tooltips. */
export function trendingReason(signals: StreamSignals): string | null {
  const s = signals;
  if (s.tipUsd >= 5) return `$${s.tipUsd.toFixed(2)} in BCH tips`;
  if (s.tipCount >= 3) return `${s.tipCount} BCH tips`;
  if (s.uniqueChatters >= 5) return `${s.uniqueChatters} people chatting`;
  if (s.chatCount >= 10) return `${s.chatCount} messages`;
  if (s.reactionCount >= 5) return `${s.reactionCount} reactions`;
  return null;
}

// ══════════════════════════════════════════════════════════════════
//  SCHEDULING
// ══════════════════════════════════════════════════════════════════

/** Countdown parts for an upcoming stream. */
export function countdownTo(startsAt: number, now = Math.floor(Date.now() / 1000)): {
  expired: boolean; days: number; hours: number; mins: number; secs: number; label: string;
} {
  const diff = startsAt - now;
  if (diff <= 0) return { expired: true, days: 0, hours: 0, mins: 0, secs: 0, label: 'Starting now' };
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = diff % 60;
  let label: string;
  if (days > 0) label = `in ${days}d ${hours}h`;
  else if (hours > 0) label = `in ${hours}h ${mins}m`;
  else if (mins > 0) label = `in ${mins}m`;
  else label = `in ${secs}s`;
  return { expired: false, days, hours, mins, secs, label };
}

/** Absolute, locale-aware start time for scheduled streams. */
export function formatScheduledTime(startsAt: number): string {
  if (!startsAt) return '';
  const d = new Date(startsAt * 1000);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today at ${time}`;
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow at ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`;
}

/** Elapsed runtime of a live stream, e.g. "1:42:07". */
export function formatDuration(startsAt: number, now = Math.floor(Date.now() / 1000)): string {
  if (!startsAt) return '';
  const secs = Math.max(0, now - startsAt);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Compact viewer count, e.g. 12400 → "12.4K". */
export function formatViewers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

// ══════════════════════════════════════════════════════════════════
//  STREAM OVERLAYS
//
//  Creator-controlled toggles, persisted locally per creator. Overlays
//  render over the player (and are also usable as a browser-source
//  style standalone view).
// ══════════════════════════════════════════════════════════════════

export type OverlayId =
  | 'recentTips'
  | 'topSupporter'
  | 'goalProgress'
  | 'donationTicker'
  | 'followerAlerts'
  | 'superChatHighlight';

export interface OverlayConfig {
  id: OverlayId;
  label: string;
  description: string;
  icon: string;
  defaultOn: boolean;
}

export const OVERLAY_CONFIGS: OverlayConfig[] = [
  { id: 'recentTips',         label: 'Recent BCH Tips',    description: 'Live list of the latest tips',            icon: 'Coins',    defaultOn: true },
  { id: 'topSupporter',       label: 'Top Supporter',      description: 'Highlights your biggest supporter',       icon: 'Trophy',   defaultOn: true },
  { id: 'goalProgress',       label: 'Goal Progress',      description: 'Progress bar toward your donation goal',  icon: 'Target',   defaultOn: true },
  { id: 'donationTicker',     label: 'Donation Ticker',    description: 'Scrolling ticker of supporters',          icon: 'Megaphone', defaultOn: false },
  { id: 'followerAlerts',     label: 'New Follower Alerts', description: 'Pops up when someone follows you',       icon: 'UserPlus', defaultOn: true },
  { id: 'superChatHighlight', label: 'Super Chat Highlights', description: 'Big animated cards for large tips',    icon: 'Sparkles', defaultOn: true },
];

const OVERLAY_KEY = 'bchnostr:stream-overlays';

export type OverlayPrefs = Record<OverlayId, boolean>;

export function defaultOverlayPrefs(): OverlayPrefs {
  const out = {} as OverlayPrefs;
  for (const c of OVERLAY_CONFIGS) out[c.id] = c.defaultOn;
  return out;
}

export function getOverlayPrefs(pubkey: string): OverlayPrefs {
  const defaults = defaultOverlayPrefs();
  if (!pubkey) return defaults;
  try {
    const raw = localStorage.getItem(`${OVERLAY_KEY}:${pubkey}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<OverlayPrefs>;
    for (const c of OVERLAY_CONFIGS) {
      if (typeof parsed[c.id] === 'boolean') defaults[c.id] = parsed[c.id] as boolean;
    }
    return defaults;
  } catch {
    return defaults;
  }
}

export function setOverlayPrefs(pubkey: string, prefs: OverlayPrefs): void {
  if (!pubkey) return;
  try {
    localStorage.setItem(`${OVERLAY_KEY}:${pubkey}`, JSON.stringify(prefs));
  } catch { /* quota / private mode — overlays fall back to defaults */ }
}

// ══════════════════════════════════════════════════════════════════
//  SCHEDULED STREAM DRAFTS (creator-side metadata)
// ══════════════════════════════════════════════════════════════════

export interface StreamDraft {
  title: string;
  description: string;
  streamUrl: string;
  image: string;
  categoryId: string;
  topics: string[];
  /** Unix seconds; 0 = go live immediately */
  startsAt: number;
}

const DRAFT_KEY = 'bchnostr:stream-draft';

export function getStreamDraft(pubkey: string): StreamDraft | null {
  if (!pubkey) return null;
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY}:${pubkey}`);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<StreamDraft>;
    return {
      title: typeof d.title === 'string' ? d.title : '',
      description: typeof d.description === 'string' ? d.description : '',
      streamUrl: typeof d.streamUrl === 'string' ? d.streamUrl : '',
      image: typeof d.image === 'string' ? d.image : '',
      categoryId: typeof d.categoryId === 'string' ? d.categoryId : '',
      topics: Array.isArray(d.topics) ? d.topics.filter(t => typeof t === 'string') : [],
      startsAt: typeof d.startsAt === 'number' ? d.startsAt : 0,
    };
  } catch {
    return null;
  }
}

export function setStreamDraft(pubkey: string, draft: StreamDraft): void {
  if (!pubkey) return;
  try {
    localStorage.setItem(`${DRAFT_KEY}:${pubkey}`, JSON.stringify(draft));
  } catch { /* ignore */ }
}

/**
 * Build the NIP-53 tag set for a stream event.
 *
 * Shared by "go live" and "schedule" so both paths always emit a
 * consistent, ecosystem-compatible kind 30311.
 */
export function buildStreamTags(opts: {
  pubkey: string;
  draft: StreamDraft;
  status: 'live' | 'planned' | 'ended';
  now: number;
  dTag?: string;
}): string[][] {
  const { pubkey, draft, status, now } = opts;
  const dTag = opts.dTag || pubkey;

  const tags: string[][] = [
    ['d', dTag],
    ['title', draft.title || 'BCH Live Stream'],
    ['summary', draft.description || ''],
    ['status', status],
  ];

  if (draft.streamUrl) tags.push(['streaming', draft.streamUrl]);
  if (draft.image) tags.push(['image', draft.image]);

  if (status === 'live') {
    tags.push(['starts', String(draft.startsAt || now)]);
    tags.push(['heartbeat', String(now)]);
  } else if (status === 'planned') {
    tags.push(['starts', String(draft.startsAt || now)]);
  } else {
    tags.push(['ends', String(now)]);
  }

  // Discovery tags. `bch-live` is what the directory queries on.
  tags.push(['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bch-live']);
  if (draft.categoryId) tags.push(['t', categoryTagValue(draft.categoryId)]);
  for (const topic of draft.topics.slice(0, 6)) {
    const clean = topic.trim().replace(/^#/, '').toLowerCase();
    if (clean && !RESERVED_TAGS.has(clean)) tags.push(['t', clean]);
  }

  tags.push(['p', pubkey, '', 'Host']);
  return tags;
}
